/** @jsx jsx */
import React, { useState, type SyntheticEvent, useEffect, useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { metrics } from '@atlassian/browser-metrics';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { toFlagId, useFlagsService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import type { OptimisticallyDeletedWebLink } from '@atlassian/jira-issue-view-common-types/src/web-links-type.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { LoomErrorCard } from './loom-error-card/index.tsx';
import { messages } from './messages.tsx';
import type { Props, DispatchProps } from './types.tsx';

export const DELETE_LOOM_VIDEO_LINK_FAILED_ID = 'deleteLoomVideoLinkFailed';
export const videoCardLoad = metrics.interaction({
	key: 'issue-view.loom-videos-card-load',
});

export const LoomVideoCard = ({
	link,
	onDeleteLink,
	onDeleteWebLinkRetry,
}: Props & DispatchProps) => {
	const { formatMessage } = useIntl();
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const { deleteFailed } = link as OptimisticallyDeletedWebLink;
	const prevDeleteFailed = usePrevious(deleteFailed);
	const { showFlag, dismissFlag } = useFlagsService();
	const [errorStatus, setErrorStatus] = useState<
		'errored' | 'fallback' | 'unauthorized' | 'forbidden' | 'not_found' | undefined
	>(undefined);
	const [isCardLoaded, setIsCardLoaded] = useState<boolean>(false);
	const [isDeleteButtonClicked, setDeleteButtonClicked] = useState<boolean>(false);

	useEffect(() => {
		isCardLoaded ? videoCardLoad.stop() : videoCardLoad.start();
	}, [isCardLoaded]);

	useEffect(() => {
		errorStatus &&
			fireErrorAnalytics({
				meta: {
					id: 'loomVideoCardLoad',
					packageName: 'jiraIssueViewBase',
					teamName: 'bento',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
	}, [errorStatus]);

	const handleError: React.ComponentProps<typeof Card>['onError'] = ({ status }) => {
		setErrorStatus(status);
		setIsCardLoaded(true);
	};

	const handleResolve = () => {
		setIsCardLoaded(true);
	};

	const handleDeleteLink = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'delete-loom-video-card-button');
		onDeleteLink(link.id);
		setDeleteButtonClicked(true);
	};

	const showDeleteErrorFlag = useCallback(() => {
		const flagId = toFlagId(DELETE_LOOM_VIDEO_LINK_FAILED_ID);
		showFlag({
			messageId:
				'issue-view-base.content.loom-videos.ui.co-use.loom-videos-list.loom-video-card.show-flag.warning.delete-error',
			messageType: 'transactional',
			id: flagId,
			title: messages.deleteErrorHeading,
			description: messages.errorBody,
			icon: (
				<WarningIcon
					label={formatMessage(messages.iconLabel)}
					color={token('color.icon.warning')}
					spacing="spacious"
				/>
			),
			type: 'warning',
			isAutoDismiss: true,
			actions: [
				{
					content: messages.retryAction,
					onClick: () => {
						dismissFlag(flagId);
						onDeleteWebLinkRetry();
					},
				},
				{
					content: messages.cancelAction,
					onClick: () => {
						dismissFlag(flagId);
					},
				},
			],
		});
	}, [onDeleteWebLinkRetry, dismissFlag, showFlag, formatMessage]);

	useEffect(() => {
		if (deleteFailed && deleteFailed !== prevDeleteFailed) {
			showDeleteErrorFlag();
			fireErrorAnalytics({
				meta: {
					id: 'loomVideoCardDeletion',
					packageName: 'jiraIssueViewBase',
					teamName: 'bento',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [deleteFailed, prevDeleteFailed, showDeleteErrorFlag]);

	const card =
		errorStatus === 'errored' || errorStatus === 'unauthorized' ? (
			<LoomErrorCard url={link.href} />
		) : (
			<Card
				url={link.href}
				appearance="embed"
				platform="web"
				frameStyle="show"
				inheritDimensions
				onError={handleError}
				onResolve={handleResolve}
			/>
		);

	return (
		<div css={wrapperStyles}>
			{card}
			{isCardLoaded && (
				<div css={closeWrapperStyles} data-show-on-hover="true">
					<IconButton
						icon={CrossIcon}
						appearance="subtle"
						label={formatMessage(messages.removeButtonLabel)}
						spacing="compact"
						onClick={handleDeleteLink}
						onFocus={(e) => {
							// after we click cancel button in delete confirmation dialog, the focus goes back to this close icon button
							if (!isDeleteButtonClicked) return;
							e.target.blur();
							setDeleteButtonClicked(false);
						}}
					/>
				</div>
			)}
		</div>
	);
};

// can't use xcss here since we have to nest CSS for .loader-wrapper
const wrapperStyles = css({
	height: '100%',
	position: 'relative',
	// this loader-wrapper override is necessary when using `inheritDimensions` prop
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> .loader-wrapper': {
		height: '100%',
	},
	// need to use nested data-* attribute selector to target show-on-hover elements
	// focus-within is for a11y purposes so when any element is focussed in wrapper (keyboard controls), the button shows
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover [data-show-on-hover="true"], &:focus-within [data-show-on-hover="true"]': {
		opacity: 1,
	},
	font: token('font.body'), // for some reason filmstripview sets font-size to 0, and it messes up the icon size, so this is needed
});

// this is a little absolute positioned element on the top-right of the smart card to show an "x" button
const closeWrapperStyles = css({
	position: 'absolute',
	width: token('space.600'),
	marginTop: token('space.050'),
	marginRight: token('space.050'),
	marginBottom: token('space.050'),
	marginLeft: token('space.050'),
	height: token('space.300'),
	zIndex: 1,
	top: 0,
	right: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	opacity: 0,
	// this is a gradient that fades to transparent on the left, but the right 55% is a solid colour
	backgroundImage: `linear-gradient(to right, ${token('color.background.neutral.subtle')}, ${token('color.background.input')} 45%, ${token('color.background.input')})`,
});
